import { FormInstance, message } from 'antd'
import { isEqual } from 'lodash'
import { PropertyType } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { TermsFlowEnum, TokenizeStatusEnum } from 'constants/pto'
import { PtoV3SellingStatusEnum } from 'constants/pto-v3'
import { usePTOV3Context } from 'context/pto-v3-context-provider'
import {
  cleanCoTrustedPartners,
  INITIAL_CO_TRUSTED_PARTNER,
  normalizeTermsContent,
} from 'extends/pto-v3.extend'
import usePrevious from 'hooks/use-previous'
import { logError, logInfo } from 'utils/logger'

interface UseEditPTOV3Props {
  form: FormInstance
}

/**
 * PTO v3 (ERC-3643) edit flow - same as PTO v2 (PATCH /admin/pto/v3/:id): no royalty,
 * symbol is read-only (set once at createPTO, the token has no setSymbol).
 * Price/fee and the pool/vault steps live on the Selling Details tab.
 */
const useEditPTOV3 = ({ form }: UseEditPTOV3Props) => {
  const [edit, setEdit] = useState(false)
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [defaultPropertyType, setDefaultPropertyType] = useState<PropertyType | null>(null)
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [switchChecked, setSwitchChecked] = useState(false)

  const { code, ptoDetail, getPTODetail } = usePTOV3Context()
  const previewPtoDetail = usePrevious(ptoDetail)

  const initFormPTODetail = useCallback(async () => {
    try {
      if (!code || !ptoDetail) return
      setSwitchChecked(ptoDetail.status === TokenizeStatusEnum.ACTIVE)
      const resGetListProperty = await AppAdmin.getListProperty()
      const types: PropertyType[] = []
      if (resGetListProperty?.data.success) {
        resGetListProperty?.data.data.map((item: PropertyType) => {
          types.push({
            ...item,
            id: Number(item.id),
          } as PropertyType)
        })
        setPropertyTypes(types)
      }
      const propertyItems: any = {}
      const selectedType = types.find((type: any) => type.id === ptoDetail?.type)
      if (selectedType) {
        setDefaultPropertyType(selectedType)
        selectedType.properties?.map((prop: any) => {
          propertyItems[`${prop}`] = ptoDetail?.propertyItems?.[`${prop}`]
        })
      }

      const formValues = {
        code,
        name: ptoDetail?.fullName,
        contractName: ptoDetail?.contractName,
        symbol: ptoDetail?.symbol,
        onchainId: ptoDetail?.onchainId,
        terms: normalizeTermsContent(ptoDetail?.terms),
        type: ptoDetail?.type,
        country: ptoDetail?.country,
        contractAddress: ptoDetail?.contractAddress,
        ownerAddress: ptoDetail?.ownerAddress,
        description: ptoDetail?.description,
        latitude: ptoDetail?.latitude,
        longitude: ptoDetail?.longitude,
        status: ptoDetail?.status === TokenizeStatusEnum.ACTIVE,
        marketPlaceSC: ptoDetail?.marketPlaceSC,
        isFeatured: ptoDetail?.isFeatured,
        isShowDetail: ptoDetail.isShowDetail,
        isShowList: ptoDetail.isShowList,
        propertyItems: propertyItems,
        tradeUnlockTime: ptoDetail?.tradeUnlockTime,
        // token cap - the API keeps the v1/v2 name maxNFTSupply
        totalSupply: ptoDetail?.maxNFTSupply,
        typeSocial: ptoDetail?.typeSocial || {},
        ptoType: ptoDetail?.ptoType,
        coTrustedPartners: ptoDetail?.coTrustedPartners?.length
          ? ptoDetail.coTrustedPartners
          : [INITIAL_CO_TRUSTED_PARTNER],
      }

      logInfo('formValues', formValues)

      form.setFieldsValue(formValues)

      setThumbnail(ptoDetail.thumbnail)
      setThumbnails(ptoDetail.thumbnails)
    } catch (error) {
      logError('Error fetching details: ', error)
    }
  }, [code, form, ptoDetail])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const {
        code,
        type,
        name,
        contractName,
        country,
        description = '',
        latitude,
        isShowDetail,
        isShowList,
        longitude,
        status,
        isFeatured,
        propertyItems,
        tradeUnlockTime,
        typeSocial,
        ptoType,
        terms,
        coTrustedPartners: formCoTrustedPartners,
      } = values

      const coTrustedPartners = cleanCoTrustedPartners(formCoTrustedPartners)

      // Body of PATCH /admin/pto/v3/:id (Swagger) - same as v2 without royaltyFee.
      // symbol / ownerAddress / totalSupply (cap) are immutable on-chain and are not sent.
      // contractAddress / onchainId / vault are managed via PATCH /admin/pto/:id/contract
      const params = {
        fullName: name,
        contractName: (contractName || '').trim(),
        sellingStatus: ptoDetail?.sellingStatus ?? PtoV3SellingStatusEnum.OFF_SALE,
        status: status ? TokenizeStatusEnum.ACTIVE : TokenizeStatusEnum.INACTIVE,
        country: country,
        description: description,
        isFeatured: !!isFeatured,
        isShowDetail,
        isShowList,
        ptoType: ptoType,
        type: type,
        latitude: latitude,
        longitude: longitude,
        propertyItems: propertyItems,
        thumbnail: thumbnail || '',
        thumbnails: thumbnails || [],
        tradeUnlockTime: tradeUnlockTime || 0,
        typeSocial: typeSocial || {},
        terms: {
          flowType: TermsFlowEnum.PURCHASE,
          content: terms || '',
        },
        coTrustedPartners,
      }
      await AppAdmin.ptoV3Update(code, params)

      message.success('PTO updated successfully!')
      getPTODetail()
      setEdit(false)
    } catch (error: any) {
      message.error(error?.message ? error?.message : 'Error while submitting form')
      logError('Error while submitting form:', error)
    }
  }

  const handleCancel = () => {
    initFormPTODetail()
    setEdit(false)
  }

  const handleEdit = () => {
    setEdit(true)
  }

  useEffect(() => {
    if (ptoDetail && !isEqual(ptoDetail, previewPtoDetail)) {
      initFormPTODetail()
    }
  }, [initFormPTODetail, previewPtoDetail, ptoDetail])

  return {
    edit,
    thumbnail,
    setThumbnail,
    thumbnails,
    setThumbnails,
    defaultPropertyType,
    propertyTypes,
    switchChecked,
    setSwitchChecked,
    ptoDetail,
    code,
    handleSubmit,
    handleCancel,
    handleEdit,
  }
}

export default useEditPTOV3
